import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  TextField,
  Paper,
  Divider,
} from "@mhome/ui";
import { CircularProgress } from "@mui/material";
import {
  Videocam as VideocamIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import { wsReadyState } from "../global-state";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";
import { useToast } from "../common/toast";
import WebRTCStream from "../ws/webrtc-stream";
import { spacingToPx } from "../lib/utils";

const TestView = forwardRef((props, ref) => {
  const wsReady = useRecoilValue(wsReadyState);
  const { successToast, errorToast } = useToast();
  const getComponentBgColor = useComponentBackgroundColor();

  const [loading, setLoading] = useState(false);
  const [webrtcActive, setWebrtcActive] = useState(false);
  const [remoteSignal, setRemoteSignal] = useState(null);
  const [localSignal, setLocalSignal] = useState(null);
  const [connectionInfo, setConnectionInfo] = useState(null);

  // WebRTC configuration from device
  const defaultWebRTCInfo = {
    sdpAnswer: {
      value: {
        id: "7cb51917-de6c-44f3-8e05-96ea33679976",
        sdp: "v=0\r\no=- 3958770411 3958770411 IN IP4 0.0.0.0\r\ns=TP\r\nc=IN IP4 3.80.26.90\r\nt=0 0\r\na=msid-semantic: WMS AudioTX\r\na=extmap-allow-mixed\r\na=group:BUNDLE 0 1\r\nm=audio 1 UDP/TLS/RTP/SAVPF 111\r\na=ice-ufrag:z0zW\r\na=sendrecv\r\na=ssrc:2954938885 cname:user788129996@host-6c0765e1\r\na=mid:0\r\na=extmap:2 http://www.webrtc.org/experiments/rtp-hdrext/abs-send-time\r\na=rtpmap:111 opus/48000/2\r\na=candidate:1 2 UDP 2015363326 3.80.26.90 34412 typ host\r\na=candidate:2 1 TCP 1015021823 3.80.26.90 9 typ host tcptype active\r\na=candidate:2 2 TCP 1015021822 3.80.26.90 9 typ host tcptype active\r\na=candidate:3 2 TCP 1010827518 3.80.26.90 34178 typ host tcptype passive\r\na=candidate:3 1 TCP 1010827519 3.80.26.90 34878 typ host tcptype passive\r\na=candidate:1 1 UDP 2015363327 3.80.26.90 34212 typ host\r\na=fmtp:111 minptime=10;useinbandfec=1\r\na=ice-pwd:b4eert+WoghlUrOZ+2V51T\r\na=fingerprint:sha-256 D0:70:D6:DC:A3:90:32:DD:8A:9A:76:B9:64:B9:FF:F9:B4:52:2F:0A:B3:FA:FA:9D:1B:D3:7F:03:00:F2:A9:46\r\na=setup:active\r\na=rtcp:9 IN IP4 0.0.0.0\r\na=rtcp-mux\r\nm=video 1 UDP/TLS/RTP/SAVPF 96\r\na=ice-ufrag:z0zW\r\na=rtcp-fb:96 goog-remb\r\na=rtcp-fb:96 ccm fir\r\na=rtcp-fb:96 nack\r\na=rtcp-fb:96 nack pli\r\na=ssrc:871171951 cname:user788129996@host-6c0765e1\r\na=mid:1\r\na=extmap:2 http://www.webrtc.org/experiments/rtp-hdrext/abs-send-time\r\na=rtpmap:96 H264/90000\r\na=sendonly\r\na=candidate:1 1 UDP 2015363327 3.80.26.90 34212 typ host\r\na=candidate:2 1 TCP 1015021823 3.80.26.90 9 typ host tcptype active\r\na=candidate:3 1 TCP 1010827519 3.80.26.90 34878 typ host tcptype passive\r\na=candidate:2 2 TCP 1015021822 3.80.26.90 9 typ host tcptype active\r\na=candidate:1 2 UDP 2015363326 3.80.26.90 34412 typ host\r\na=candidate:3 2 TCP 1010827518 3.80.26.90 34178 typ host tcptype passive\r\na=fmtp:96 level-asymmetry-allowed=1;packetization-mode=1;profile-level-id=42e01f\r\na=ice-pwd:b4eert+WoghlUrOZ+2V51T\r\na=fingerprint:sha-256 D0:70:D6:DC:A3:90:32:DD:8A:9A:76:B9:64:B9:FF:F9:B4:52:2F:0A:B3:FA:FA:9D:1B:D3:7F:03:00:F2:A9:46\r\na=setup:active\r\na=rtcp:9 IN IP4 0.0.0.0\r\na=rtcp-mux\r\n",
      },
      timestamp: "2025-06-13T02:26:51.462Z",
    },
    talkback: {
      value: false,
      timestamp: "2025-11-16T05:42:45.172Z",
    },
    turnInfo: {
      value: null,
    },
    supportedFeatures: {
      value: {
        bundle: true,
        order: "audio/video",
        audio: "sendrecv",
        video: "recvonly",
      },
      timestamp: "2025-11-16T05:42:45.172Z",
    },
    sdpOffer: {
      value: null,
    },
    deviceIce: {
      value: null,
    },
    talkbackDuplex: {
      value: null,
    },
    audioOnly: {
      value: false,
      timestamp: "2025-11-16T05:42:45.172Z",
    },
    stunUrl: {
      value: "stun:us-stun.tplinkcloud.com:3478",
      timestamp: "2025-11-16T05:42:45.172Z",
    },
    standbyMode: {
      value: null,
    },
  };

  const [webrtcInfo, setWebrtcInfo] = useState(defaultWebRTCInfo);
  const [configInput, setConfigInput] = useState(
    JSON.stringify(defaultWebRTCInfo, null, 2)
  );
  const [configValid, setConfigValid] = useState(true);
  const [customSdp, setCustomSdp] = useState("");

  // Validate config input
  useEffect(() => {
    if (!configInput.trim()) {
      setConfigValid(false);
      return;
    }
    try {
      const parsed = JSON.parse(configInput);
      if (parsed && typeof parsed === "object") {
        setWebrtcInfo(parsed);
        setConfigValid(true);
      } else {
        setConfigValid(false);
      }
    } catch (err) {
      setConfigValid(false);
    }
  }, [configInput]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    handleAdd: handleStartWebRTC,
  }));

  const handleStartWebRTC = () => {
    setWebrtcActive(true);
    successToast("Starting WebRTC connection...");
  };

  const handleStopWebRTC = () => {
    setWebrtcActive(false);
    setRemoteSignal(null);
    setLocalSignal(null);
    setConnectionInfo(null);
    successToast("WebRTC connection stopped");
  };

  const handleSignal = (signal) => {
    console.log("Local signal generated:", signal);
    setLocalSignal(signal);
    setConnectionInfo((prev) => ({
      ...prev,
      localSignal: signal,
      signalType: signal.type,
    }));
  };

  const handleStream = (stream) => {
    console.log("Remote stream received:", stream);
    successToast("Remote stream connected!");
    setConnectionInfo((prev) => ({
      ...prev,
      streamActive: true,
      streamId: stream.id,
      tracks: stream.getTracks().length,
    }));
  };

  const handleConnect = () => {
    console.log("WebRTC connection established");
    successToast("WebRTC connected!");
    setConnectionInfo((prev) => ({
      ...prev,
      connected: true,
      connectedAt: new Date().toISOString(),
    }));
  };

  const handleClose = () => {
    console.log("WebRTC connection closed");
    errorToast("WebRTC connection closed");
    setConnectionInfo((prev) => ({
      ...prev,
      connected: false,
      disconnectedAt: new Date().toISOString(),
    }));
  };

  const handleError = (error) => {
    console.error("WebRTC error:", error);
    errorToast(`WebRTC error: ${error.message}`);
    setConnectionInfo((prev) => ({
      ...prev,
      error: error.message,
      errorAt: new Date().toISOString(),
    }));
  };

  const handleUseSdpAnswer = () => {
    if (webrtcInfo.sdpAnswer?.value) {
      setRemoteSignal({
        type: "answer",
        sdp: webrtcInfo.sdpAnswer.value.sdp,
      });
      successToast("SDP Answer applied");
    } else {
      errorToast("No SDP Answer available");
    }
  };

  const handleUseCustomSdp = () => {
    try {
      const signal = JSON.parse(customSdp);
      setRemoteSignal(signal);
      successToast("Custom SDP applied");
    } catch (err) {
      errorToast("Invalid JSON format");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: spacingToPx(3),
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <div
      style={{
        flexGrow: 1,
        margin: spacingToPx(2),
        paddingBottom: spacingToPx(12),
      }}
    >
      <Typography
        variant="h5"
        style={{ marginBottom: spacingToPx(2), fontWeight: 600 }}
      >
        Development Features
      </Typography>

      {/* WebRTC Section */}
      <Card
        elevation={0}
        style={{
          backgroundColor: getComponentBgColor("hsl(var(--card))"),
          marginBottom: spacingToPx(3),
        }}
      >
        <CardContent>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: spacingToPx(2),
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: spacingToPx(1),
              }}
            >
              <VideocamIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                WebRTC Video Stream
              </Typography>
            </div>
            {webrtcActive ? (
              <Button
                variant="contained"
                color="error"
                onClick={handleStopWebRTC}
                size="small"
              >
                Stop
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleStartWebRTC}
                size="small"
                disabled={!configValid}
              >
                Start
              </Button>
            )}
          </div>

          <Divider style={{ marginBottom: spacingToPx(2) }} />

          {/* Config Input - Always visible */}
          <div style={{ marginBottom: spacingToPx(2) }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: spacingToPx(1),
              }}
            >
              <Typography variant="subtitle2">
                WebRTC Configuration (Paste or Edit JSON)
              </Typography>
              <Chip
                label={configValid ? "Valid" : "Invalid JSON"}
                color={configValid ? "success" : "error"}
                size="small"
              />
            </div>
            <TextField
              fullWidth
              multiline
              rows={8}
              value={configInput}
              onChange={(e) => setConfigInput(e.target.value)}
              placeholder="Paste WebRTC configuration JSON here..."
              error={!configValid}
              helperText={
                !configValid
                  ? "Invalid JSON format. Please check your input."
                  : "Configuration is valid. Click Start to begin."
              }
              style={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
              }}
            />
          </div>

          <Divider style={{ marginBottom: spacingToPx(2) }} />

          {webrtcActive ? (
            <>
              {/* WebRTC Stream Display */}
              <div
                style={{
                  height: 400,
                  marginBottom: spacingToPx(2),
                  borderRadius: spacingToPx(2),
                  overflow: "hidden",
                  backgroundColor: "#000",
                }}
              >
                <WebRTCStream
                  initiator={true}
                  signalData={remoteSignal}
                  onSignal={handleSignal}
                  onStream={handleStream}
                  onConnect={handleConnect}
                  onClose={handleClose}
                  onError={handleError}
                  localVideo={true}
                  remoteVideo={true}
                  showControls={true}
                  autoConnect={true}
                />
              </div>

              {/* Connection Controls */}
              <div style={{ marginBottom: spacingToPx(2) }}>
                <Typography variant="subtitle2" gutterBottom>
                  Connection Controls
                </Typography>
                <div
                  style={{
                    display: "flex",
                    gap: spacingToPx(1),
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleUseSdpAnswer}
                    disabled={!webrtcInfo.sdpAnswer?.value}
                  >
                    Use Device SDP Answer
                  </Button>
                </div>
              </div>

              {/* Local Signal Display */}
              {localSignal && (
                <div style={{ marginBottom: spacingToPx(2) }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Local Signal (Send to remote peer)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={JSON.stringify(localSignal, null, 2)}
                    InputProps={{
                      readOnly: true,
                    }}
                    onClick={(e) => {
                      e.target.select();
                      navigator.clipboard.writeText(
                        JSON.stringify(localSignal)
                      );
                      successToast("Copied to clipboard");
                    }}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              )}

              {/* Custom SDP Input */}
              <div style={{ marginBottom: spacingToPx(2) }}>
                <Typography variant="subtitle2" gutterBottom>
                  Paste Remote Signal (JSON)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={customSdp}
                  onChange={(e) => setCustomSdp(e.target.value)}
                  placeholder='{"type":"answer","sdp":"..."}'
                  style={{ marginBottom: spacingToPx(1) }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleUseCustomSdp}
                  disabled={!customSdp}
                >
                  Apply Remote Signal
                </Button>
              </div>

              {/* Connection Info */}
              {connectionInfo && (
                <Paper
                  className="bg-accent"
                  style={{
                    padding: spacingToPx(2),
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Connection Info
                  </Typography>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: spacingToPx(1),
                    }}
                  >
                    <Chip
                      label={
                        connectionInfo.connected ? "Connected" : "Connecting"
                      }
                      color={connectionInfo.connected ? "success" : "warning"}
                      size="small"
                    />
                    {connectionInfo.signalType && (
                      <Chip
                        label={`Signal: ${connectionInfo.signalType}`}
                        size="small"
                      />
                    )}
                    {connectionInfo.streamActive && (
                      <Chip
                        label="Stream Active"
                        color="success"
                        size="small"
                      />
                    )}
                    {connectionInfo.tracks && (
                      <Chip
                        label={`${connectionInfo.tracks} tracks`}
                        size="small"
                      />
                    )}
                    {connectionInfo.error && (
                      <Chip
                        label={`Error: ${connectionInfo.error}`}
                        color="error"
                        size="small"
                      />
                    )}
                  </div>
                </Paper>
              )}
            </>
          ) : (
            <div
              style={{
                textAlign: "center",
                paddingTop: spacingToPx(4),
                paddingBottom: spacingToPx(4),
              }}
            >
              <VideocamIcon
                className="text-muted-foreground"
                style={{
                  fontSize: 64,
                }}
              />
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ marginTop: spacingToPx(2) }}
              >
                Click Start to begin WebRTC video stream
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Parsed Configuration Info */}
      {configValid && (
        <Card
          elevation={0}
          style={{
            backgroundColor: getComponentBgColor("hsl(var(--card))"),
            marginBottom: spacingToPx(3),
          }}
        >
          <CardContent>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: spacingToPx(1),
                marginBottom: spacingToPx(2),
              }}
            >
              <InfoIcon color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Parsed Configuration
              </Typography>
            </div>

            <Divider style={{ marginBottom: spacingToPx(2) }} />

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: spacingToPx(1),
                marginBottom: spacingToPx(2),
              }}
            >
              <Chip
                label={`Audio Only: ${
                  webrtcInfo.audioOnly?.value ? "Yes" : "No"
                }`}
                size="small"
              />
              <Chip
                label={`Talkback: ${webrtcInfo.talkback?.value ? "Yes" : "No"}`}
                size="small"
              />
              {webrtcInfo.supportedFeatures?.value && (
                <>
                  <Chip
                    label={`Bundle: ${
                      webrtcInfo.supportedFeatures.value.bundle ? "Yes" : "No"
                    }`}
                    size="small"
                  />
                  <Chip
                    label={`Audio: ${webrtcInfo.supportedFeatures.value.audio}`}
                    size="small"
                  />
                  <Chip
                    label={`Video: ${webrtcInfo.supportedFeatures.value.video}`}
                    size="small"
                  />
                </>
              )}
            </div>

            {webrtcInfo.stunUrl?.value && (
              <div style={{ marginBottom: spacingToPx(2) }}>
                <Typography variant="subtitle2" gutterBottom>
                  STUN Server
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    fontFamily: "monospace",
                    backgroundColor: "hsl(var(--accent))",
                    padding: spacingToPx(1),
                    borderRadius: spacingToPx(1),
                  }}
                >
                  {webrtcInfo.stunUrl.value}
                </Typography>
              </div>
            )}

            {webrtcInfo.sdpAnswer?.value?.sdp && (
              <div>
                <Typography variant="subtitle2" gutterBottom>
                  SDP Answer (Device)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={webrtcInfo.sdpAnswer.value.sdp}
                  InputProps={{
                    readOnly: true,
                    style: { fontFamily: "monospace", fontSize: "0.75rem" },
                  }}
                  onClick={(e) => {
                    e.target.select();
                    navigator.clipboard.writeText(
                      webrtcInfo.sdpAnswer.value.sdp
                    );
                    successToast("SDP copied to clipboard");
                  }}
                  style={{ cursor: "pointer" }}
                />
                {webrtcInfo.sdpAnswer.value.id && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginTop: spacingToPx(1), display: "block" }}
                  >
                    ID: {webrtcInfo.sdpAnswer.value.id}
                  </Typography>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
});

TestView.displayName = "TestView";

export default TestView;
